import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { useViewingEmployee } from '@hooks/useViewingEmployee';
import { timesheetService } from '@services/timesheetService';
import { taskService } from '@services/taskService';
import type { Task, AuthorizedTaskMap, Balances, TimesheetHours, LeaveAccumulationMap } from '@/types';
import {
  getPreviousSaturday,
  formatDateForAPI,
  getWeekDates,
  getDayHeader,
  navigateWeek,
} from '@utils/dateUtils';
import { calculateWeekTotal } from '@utils/timesheetCalculations';
import DateNavigation from './DateNavigation';
import TimesheetRow from './TimesheetRow';
import AddTaskModal from './AddTaskModal';
import AuthorizedTasksModal from './AuthorizedTasksModal';
import AllTasksModal from './AllTasksModal';
import YearEndRolloverModal from './YearEndRolloverModal';
import toast from 'react-hot-toast';

const TimesheetTab: FC = () => {
  const location = useLocation();
  const { userInfo } = useAuth();
  const { viewingEmployee } = useViewingEmployee();
  const [currentWeek, setCurrentWeek] = useState<Date>(getPreviousSaturday(new Date()));
  const [timesheetHours, setTimesheetHours] = useState<TimesheetHours>({});
  const [originalTimesheetHours, setOriginalTimesheetHours] = useState<TimesheetHours>({}); // Store original hours for balance calculation
  const [authorizedTasks, setAuthorizedTasks] = useState<AuthorizedTaskMap>({});
  const [balances, setBalances] = useState<Balances>({});
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [leaveAccumulation, setLeaveAccumulation] = useState<LeaveAccumulationMap>({});
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal states
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [showAuthorizedTasksModal, setShowAuthorizedTasksModal] = useState(false);
  const [showAllTasksModal, setShowAllTasksModal] = useState(false);
  const [showYearEndRolloverModal, setShowYearEndRolloverModal] = useState(false);

  const weekDates = getWeekDates(currentWeek);
  const weekDateStr = formatDateForAPI(currentWeek);

  // Helper function to determine which tasks are authorized for the current week
  const getAuthorizedTaskNamesForWeek = useCallback((tasksData: AuthorizedTaskMap): Set<string> => {
    const weekStartNum = parseInt(weekDateStr);
    const weekEndDate = new Date(currentWeek);
    weekEndDate.setDate(weekEndDate.getDate() + 6);
    const weekEndNum = parseInt(formatDateForAPI(weekEndDate));

    const authorizedTaskNamesForWeek = new Set<string>();

    Object.entries(tasksData).forEach(([taskName, authTask]) => {
      const startDate = parseInt(authTask.M.start_date.N);
      const endDate = parseInt(authTask.M.end_date.N);

      // Check if task authorization period overlaps with current week
      let isAuthorized = false;
      if (endDate === 0) {
        isAuthorized = weekEndNum >= startDate;
      } else {
        isAuthorized = weekStartNum <= endDate && weekEndNum >= startDate;
      }

      if (isAuthorized) {
        authorizedTaskNamesForWeek.add(taskName);
      }
    });

    return authorizedTaskNamesForWeek;
  }, [currentWeek, weekDateStr]);

  // Load timesheet data with retry logic
  const loadTimesheet = useCallback(async () => {
    if (!viewingEmployee?.employee_id) return;

    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
      try {
        const [timesheetData, tasksData, balancesData] = await Promise.all([
          timesheetService.getTimesheet(viewingEmployee.employee_id, weekDateStr),
          taskService.getAuthorizedTasks(viewingEmployee.employee_id),
          timesheetService.getBalances(viewingEmployee.employee_id),
        ]);

        // Parse timesheet hours
        const hours: TimesheetHours = {};
        if (timesheetData.Item?.hours?.M) {
          Object.entries(timesheetData.Item.hours.M).forEach(([taskName, hoursList]) => {
            if (hoursList.L) {
              hours[taskName] = hoursList.L.map((h) => h.N);
            }
          });
        }

        setAuthorizedTasks(tasksData);
        setBalances(balancesData);

        // Determine which tasks should be shown for the current week
        // Include all authorized tasks for this week, not just those with hours
        const authorizedTaskNamesForWeek = getAuthorizedTaskNamesForWeek(tasksData);

        setSelectedTasks(authorizedTaskNamesForWeek);

        // Initialize hours for tasks that don't have hours yet
        const updatedHours = { ...hours };
        authorizedTaskNamesForWeek.forEach(taskName => {
          if (!updatedHours[taskName]) {
            updatedHours[taskName] = Array(7).fill('0');
          }
        });

        // Store both original (for balance calculation) and current hours
        setOriginalTimesheetHours({ ...updatedHours });
        setTimesheetHours(updatedHours);

        setHasChanges(false);

        // Success - break out of retry loop
        return;
      } catch (error) {
        attempt++;
        console.error(`Error loading timesheet (attempt ${attempt}/${maxRetries}):`, error);

        if (attempt >= maxRetries) {
          // Final attempt failed
          toast.error('Failed to load timesheet data after multiple attempts');
        } else {
          // Wait before retrying (exponential backoff: 500ms, 1000ms)
          await new Promise(resolve => setTimeout(resolve, 500 * attempt));
        }
      }
    }
  }, [viewingEmployee?.employee_id, weekDateStr, getAuthorizedTaskNamesForWeek]);

  // Load all tasks for modals
  const loadAllTasks = useCallback(async () => {
    try {
      const tasks = await taskService.getAllTasks();
      setAllTasks(tasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  }, []);

  // Load leave accumulation codes
  const loadLeaveAccumulation = useCallback(async () => {
    try {
      const response = await taskService.getLeaveAccumulation();
      const map: LeaveAccumulationMap = {};
      response.Items.forEach(item => {
        const year = parseInt(item.year.S);
        map[year] = {
          pto_code: item.pto_code.S,
          comp_code: item.comp_code.S,
        };
      });
      setLeaveAccumulation(map);
    } catch (error) {
      console.error('Error loading leave accumulation:', error);
    }
  }, []);

  useEffect(() => {
    loadAllTasks();
    loadLeaveAccumulation();
  }, [loadAllTasks, loadLeaveAccumulation]);

  // Reload timesheet when viewing employee changes (via URL parameter or week changes)
  useEffect(() => {
    if (viewingEmployee?.employee_id) {
      loadTimesheet();
    }
  }, [viewingEmployee?.employee_id, currentWeek, loadTimesheet]);

  // Also watch for location.search changes directly
  useEffect(() => {
    if (viewingEmployee?.employee_id) {
      loadTimesheet();
    }
  }, [location.search]);

  // Handle week navigation
  const handlePreviousWeek = () => {
    setCurrentWeek(navigateWeek(currentWeek, -1));
  };

  const handleNextWeek = () => {
    setCurrentWeek(navigateWeek(currentWeek, 1));
  };

  const handleWeekChange = (newWeek: Date) => {
    setCurrentWeek(getPreviousSaturday(newWeek));
  };

  // Handle hour changes
  const handleHourChange = (taskName: string, dayIndex: number, value: string) => {
    setTimesheetHours((prev) => {
      const taskHours = prev[taskName] || Array(7).fill('0');
      const newTaskHours = [...taskHours];
      newTaskHours[dayIndex] = value;

      return {
        ...prev,
        [taskName]: newTaskHours,
      };
    });
    setHasChanges(true);
  };

  // Save timesheet
  const handleSave = async () => {
    if (!viewingEmployee?.employee_id) return;

    setIsSaving(true);
    try {
      await timesheetService.saveTimesheet(viewingEmployee.employee_id, weekDateStr, timesheetHours);
      toast.success('Timesheet saved successfully');
      setHasChanges(false);
      // Reload to get updated balances
      await loadTimesheet();
    } catch (error) {
      console.error('Error saving timesheet:', error);
      toast.error('Failed to save timesheet');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle task selection
  const handleTasksSelected = (taskNames: string[]) => {
    const newSelected = new Set(taskNames);
    setSelectedTasks(newSelected);

    // Initialize hours for newly added tasks
    setTimesheetHours((prev) => {
      const updated = { ...prev };
      taskNames.forEach((taskName) => {
        if (!updated[taskName]) {
          updated[taskName] = Array(7).fill('0');
        }
      });
      // Remove tasks that were deselected
      Object.keys(prev).forEach((taskName) => {
        if (!newSelected.has(taskName)) {
          delete updated[taskName];
        }
      });
      return updated;
    });

    setHasChanges(true);
  };

  // Get filtered tasks that are authorized for current week
  const getAuthorizedTasksForWeek = (): Task[] => {
    // Get the set of authorized task names for the current week
    const authorizedTaskNames = getAuthorizedTaskNamesForWeek(authorizedTasks);

    // Filter allTasks to only include those that are authorized
    const filtered = allTasks.filter((task) => {
      const taskName = task.name.S;
      return authorizedTaskNames.has(taskName);
    });

    return filtered;
  };

  // Calculate total hours for the week
  const weekTotal = calculateWeekTotal(timesheetHours);

  // Calculate billable hours and accrual adjustments for PTO/Comp
  const calculateAccrualAdjustments = useCallback((): { [taskName: string]: number } => {
    // Calculate billable hours per year (to handle weeks spanning two years)
    const originalBillablePerYear: { [year: number]: number } = {};
    const currentBillablePerYear: { [year: number]: number } = {};

    // Helper to calculate billable hours per day and group by year
    const calculateBillablePerYear = (hoursData: TimesheetHours, targetMap: { [year: number]: number }) => {
      Object.entries(hoursData).forEach(([taskName, hours]) => {
        const task = allTasks.find(t => t.name.S === taskName);
        if (task && !task.leave.BOOL && !task.overhead.BOOL) {
          // This is a billable task - calculate hours per day
          hours.forEach((dayHours, dayIndex) => {
            const dayDate = new Date(currentWeek);
            dayDate.setDate(dayDate.getDate() + dayIndex);
            const year = dayDate.getFullYear();

            targetMap[year] = (targetMap[year] || 0) + parseFloat(dayHours || '0');
          });
        }
      });
    };

    calculateBillablePerYear(originalTimesheetHours, originalBillablePerYear);
    calculateBillablePerYear(timesheetHours, currentBillablePerYear);

    // Calculate accrual for a given billable hours amount
    const calculateAccrualForBillable = (billable: number) => {
      const accrual = { pto: 0, comp: 0 };
      if (billable > 40) {
        accrual.pto = 8;
        accrual.comp = billable - 40;
      } else {
        accrual.pto = billable / 5;
        accrual.comp = 0;
      }
      return accrual;
    };

    const adjustments: { [taskName: string]: number } = {};

    // Calculate adjustments for each year that appears in either original or current
    const allYears = new Set([
      ...Object.keys(originalBillablePerYear).map(y => parseInt(y)),
      ...Object.keys(currentBillablePerYear).map(y => parseInt(y))
    ]);

    allYears.forEach(year => {
      const originalBillable = originalBillablePerYear[year] || 0;
      const currentBillable = currentBillablePerYear[year] || 0;

      const originalAccrual = calculateAccrualForBillable(originalBillable);
      const currentAccrual = calculateAccrualForBillable(currentBillable);

      const ptoDiff = currentAccrual.pto - originalAccrual.pto;
      const compDiff = currentAccrual.comp - originalAccrual.comp;

      // Look up the correct PTO and Comp codes from leave_accumulation
      const leaveCodes = leaveAccumulation[year];
      if (leaveCodes) {
        adjustments[leaveCodes.pto_code] = ptoDiff;
        adjustments[leaveCodes.comp_code] = compDiff;
      }
    });

    return adjustments;
  }, [originalTimesheetHours, timesheetHours, allTasks, currentWeek, leaveAccumulation]);

  const accrualAdjustments = calculateAccrualAdjustments();

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated tasks
  const taskArray = Array.from(selectedTasks);
  const paginatedTasks = taskArray.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <div className="container-fluid">
        <div className="row bg-controls mb-3">
          <div className="col-12 col-md-4 mb-2 mb-md-0">
            <DateNavigation
              currentWeek={currentWeek}
              onPreviousWeek={handlePreviousWeek}
              onNextWeek={handleNextWeek}
              onWeekChange={handleWeekChange}
            />
          </div>
          {userInfo?.admin && (
            <div className="col-12 col-md-8 d-flex justify-content-md-end gap-2">
              <Button variant="primary" onClick={() => setShowAuthorizedTasksModal(true)}>
                Select Authorized Tasks
              </Button>
              <Button variant="primary" onClick={() => setShowAllTasksModal(true)}>
                Manage All Tasks
              </Button>
              <Button variant="warning" onClick={() => setShowYearEndRolloverModal(true)}>
                Year-End Rollover
              </Button>
            </div>
          )}
        </div>

        <div className="row">
          <div className="col-12">
            <TableContainer component={Paper}>
              <Table className="timesheet-table">
                <TableHead>
                  <TableRow>
                    <TableCell className="sticky-col">Task</TableCell>
                    {weekDates.map((date, index) => (
                      <TableCell key={index} className="day-col">
                        {getDayHeader(date, index)}
                      </TableCell>
                    ))}
                    <TableCell className="total-col">Total</TableCell>
                    <TableCell className="balance-col">Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTasks.map((taskName) => (
                    <TimesheetRow
                      key={taskName}
                      taskName={taskName}
                      hours={timesheetHours[taskName] || Array(7).fill('0')}
                      originalHours={originalTimesheetHours[taskName] || Array(7).fill('0')}
                      balance={balances[taskName]}
                      accrualAdjustment={accrualAdjustments[taskName] || 0}
                      onHourChange={handleHourChange}
                    />
                  ))}
                  <TableRow>
                    <TableCell className="sticky-col">
                    </TableCell>
                    {Array(6).fill(0).map((_, i) => (
                      <TableCell key={i} className="day-col"></TableCell>
                    ))}
                    <TableCell className="day-col">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        disabled={isSaving || !hasChanges}
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </TableCell>
                    <TableCell className="total-col">
                      <input
                        type="number"
                        className="form-control"
                        value={weekTotal}
                        disabled
                      />
                    </TableCell>
                    <TableCell className="balance-col"></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={taskArray.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </TableContainer>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AddTaskModal
        show={showAddTaskModal}
        onHide={() => setShowAddTaskModal(false)}
        tasks={getAuthorizedTasksForWeek()}
        selectedTasks={selectedTasks}
        onSave={handleTasksSelected}
      />

      <AuthorizedTasksModal
        show={showAuthorizedTasksModal}
        onHide={() => setShowAuthorizedTasksModal(false)}
        employeeId={viewingEmployee?.employee_id || ''}
        onSave={() => {
          loadTimesheet();
          loadAllTasks();
        }}
      />

      <AllTasksModal
        show={showAllTasksModal}
        onHide={() => setShowAllTasksModal(false)}
        onSave={() => {
          loadAllTasks();
          loadTimesheet();
        }}
      />

      <YearEndRolloverModal
        show={showYearEndRolloverModal}
        onHide={() => setShowYearEndRolloverModal(false)}
      />
    </>
  );
};

export default TimesheetTab;
